import { ui } from "@patient-watch/ui";
import { SurgeryFormFields } from "@/features/events/features/surgeries/components/SurgeryFormFields";
import {
    searchForTemplateAiWizardNavigation,
    useTemplateAiWizardSearch,
} from "@/features/ai-builder/useTemplateAiDraftStore";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useFinalizeTemplateWizard } from "@/features/templates/hooks/useFinalizeTemplateWizard";
import { useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { templateSettingsSchema } from "@patient-watch/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { useAuth } from "@/utils/auth";

type SurgeryDetailsValues = Pick<
    z.infer<typeof templateSettingsSchema>,
    "side" | "orthopaedic_area" | "surgery_option_id"
>;

const SurgeryDetailsPage = () => {
    const auth = useAuth();
    const templatesCopy = useTemplatesCopy();
    const { templateId } = Route.useParams();
    const search = useTemplateAiWizardSearch(templateId, Route.useSearch());
    const side = search.side;
    const orthopaedic_area = search.orthopaedic_area;
    const surgery_option_id = search.surgery_option_id;
    const navigate = useNavigate();
    const { markStepComplete, nextStep, setCurrentStep, getNextStepRoute, getPreviousStepRoute, currentStep } =
        useTemplateNavigationStore();
    const { finalize, isSubmitting, isTemplateOwnerLookupBlockingSubmit } = useFinalizeTemplateWizard(
        templateId,
        search
    );

    useEffect(() => {
        setCurrentStep("surgery-details");
    }, [setCurrentStep]);

    const form = useForm<SurgeryDetailsValues>({
        resolver: zodResolver(
            templateSettingsSchema.pick({
                side: true,
                orthopaedic_area: true,
                surgery_option_id: true,
            })
        ),
        defaultValues: {
            side: side ?? undefined,
            orthopaedic_area: orthopaedic_area ?? undefined,
            surgery_option_id: surgery_option_id ?? undefined,
        },
    });

    const handleNext = (data: SurgeryDetailsValues) => {
        markStepComplete(currentStep);
        const nextRoute = getNextStepRoute(currentStep, { templateId });
        if (nextRoute) {
            nextStep();
            const nextSettings = { ...search, ...data };
            const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
            if (!wizardSearchPayload) return;
            navigate({
                ...nextRoute,
                search: wizardSearchPayload,
            });
        } else {
            finalize(data);
        }
    };

    const handlePrevious = () => {
        const prevRoute = getPreviousStepRoute(currentStep, { templateId });
        if (prevRoute) {
            const nextSettings = { ...search, ...form.getValues() };
            const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
            if (!wizardSearchPayload) return;
            navigate({
                ...prevRoute,
                search: wizardSearchPayload,
            });
        }
    };

    return (
        <ui.Form {...form}>
            <form onSubmit={form.handleSubmit(handleNext)} className="space-y-6">
                <SurgeryFormFields
                    sideFieldName="side"
                    orthopaedicAreaFieldName="orthopaedic_area"
                    surgeryOptionIdFieldName="surgery_option_id"
                />

                <TemplateStepFooter
                    onBack={handlePrevious}
                    submitPrimary
                    primaryAction="save"
                    primaryLabel={templatesCopy.saveTemplate}
                    isLoadingPrimary={isSubmitting}
                    disabledPrimary={isTemplateOwnerLookupBlockingSubmit}
                />
            </form>
        </ui.Form>
    );
};

const surgeryDetailsSearchSchema = templateSettingsSchema.partial().pick({
    side: true,
    orthopaedic_area: true,
    surgery_option_id: true,
});

export const Route = createFileRoute("/_app/templates/$templateId/events/surgery/details")({
    component: SurgeryDetailsPage,
    validateSearch: (search) => surgeryDetailsSearchSchema.parse(search),
});
